import { SLOTS_PER_DAY, formatSlotTime } from '@betreuung/shared';
import { Minus, Plus } from 'lucide-react';
import { de } from '@/i18n/de';

export interface TimeStepperProps {
  /** Position inside the day, `0 … 48`. 48 is rendered as "24:00". */
  slotInDay: number;
  onChange: (slotInDay: number) => void;
  /** The end field may reach 24:00; the start field may not. */
  allowEndOfDay?: boolean;
}

/**
 * Half-hour stepper. There is no free text entry on purpose: every time in the
 * app snaps to the 30-minute grid, and two buttons cannot produce an invalid
 * value.
 */
export function TimeStepper({ slotInDay, onChange, allowEndOfDay = false }: TimeStepperProps) {
  const max = allowEndOfDay ? SLOTS_PER_DAY : SLOTS_PER_DAY - 1;

  const step = (delta: number) => {
    const next = slotInDay + delta;
    // Clamping rather than wrapping: rolling over to the next day silently would
    // move the date field behind the user's back.
    onChange(Math.min(max, Math.max(0, next)));
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label={de.modal.decrease}
        onClick={() => step(-1)}
        disabled={slotInDay <= 0}
        className="hit-44 flex h-[38px] w-[34px] items-center justify-center rounded-control border border-line text-fg-tertiary disabled:opacity-40"
      >
        <Minus className="size-4" />
      </button>
      <span className="w-[52px] text-center font-mono text-action font-semibold text-fg">
        {formatSlotTime(slotInDay)}
      </span>
      <button
        type="button"
        aria-label={de.modal.increase}
        onClick={() => step(1)}
        disabled={slotInDay >= max}
        className="hit-44 flex h-[38px] w-[34px] items-center justify-center rounded-control border border-line text-fg-tertiary disabled:opacity-40"
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}
